/**
 * Create/edit form for knowledge base tags.
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, Input, Switch, Button, Card, Tabs, Space, message } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useKBTags, useCreateTag, useUpdateTag } from '../hooks/useKnowledgeBase';
import { LanguageSettingsControl } from '../components/knowledgeBase';
import { DEFAULT_LANGUAGE_SETTINGS, validateLanguageSettings, sanitizeLanguageSettings } from '@municipality/types';
import { AVAILABLE_LANGUAGES } from '@municipality/types';
import type { KBTagTranslation } from '@municipality/types';

type TagFormData = {
  translations: Record<string, { name: string }>;
  languageSettings: {
    primaryLanguage: string;
    secondaryLanguages: string[];
    isMultilingual: boolean;
  };
  order: number;
  isActive: boolean;
};

const schema = yup.object({
  translations: yup.object().test(
    'primary-name-required',
    'Name is required in primary language',
    function (value) {
      const parent = this.parent as TagFormData;
      const primaryLang = parent?.languageSettings?.primaryLanguage;
      if (!value || typeof value !== 'object' || !primaryLang) return false;
      const tr = value as Record<string, { name?: string }>;
      const primary = tr[primaryLang]?.name?.trim();
      return !!primary;
    }
  ),
  languageSettings: yup.object({
    primaryLanguage: yup.string().required(),
    secondaryLanguages: yup.array().of(yup.string()).default([]),
    isMultilingual: yup.boolean().default(false),
  }),
  order: yup.number().required(),
  isActive: yup.boolean().required(),
});

export const KnowledgeBaseTagForm: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;

  const { data: tags, isLoading } = useKBTags();
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();

  const tag = tags?.find((tagItem) => tagItem.id === id);

  const { control, handleSubmit, watch, setValue, getValues, formState } = useForm<TagFormData>({
    resolver: yupResolver(schema) as Resolver<TagFormData>,
    defaultValues: {
      translations: { en: { name: '' } },
      languageSettings: DEFAULT_LANGUAGE_SETTINGS,
      order: 0,
      isActive: true,
    },
  });

  const languageSettings = watch('languageSettings');

  useEffect(() => {
    if (tag) {
      setValue('translations', tag.translations as Record<string, { name: string }>);
      setValue('languageSettings', tag.languageSettings);
      setValue('order', tag.order);
      setValue('isActive', tag.isActive);
    } else if (!isCreate && !isLoading && !tags?.some((tagItem) => tagItem.id === id)) {
      message.error(t('knowledgeBase:errors.loadError'));
    } else if (isCreate && tags?.length) {
      const maxOrder = Math.max(-1, ...tags.map((tagItem) => tagItem.order));
      setValue('order', maxOrder + 1);
    }
  }, [tag, isCreate, isLoading, tags, id, setValue, t]);

  useEffect(() => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const secondary = languageSettings?.secondaryLanguages ?? [];
    const langs = [primary, ...secondary.filter((l) => l !== primary)];
    const prev = getValues('translations') as Record<string, { name: string }>;
    const next = { ...prev };
    let changed = false;
    langs.forEach((lang) => {
      if (!next[lang]) {
        next[lang] = { name: '' };
        changed = true;
      }
    });
    if (changed) setValue('translations', next);
  }, [languageSettings?.primaryLanguage, languageSettings?.secondaryLanguages, setValue, getValues]);

  const getTabItems = (): { key: string; label: string; children: React.ReactNode }[] => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const secondary = languageSettings?.secondaryLanguages ?? [];
    const langs = [primary, ...secondary.filter((l) => l !== primary)];

    if (langs.length === 0) {
      return [
        {
          key: 'en',
          label: 'English',
          children: (
            <Controller
              name="translations.en.name"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:tags.name')} required>
                  <Input {...field} placeholder={t('knowledgeBase:tags.name')} />
                </Form.Item>
              )}
            />
          ),
        },
      ];
    }

    return langs.map((langCode) => {
      const opt = AVAILABLE_LANGUAGES.find((l) => l.code === langCode);
      return {
        key: langCode,
        label: opt?.nativeName ?? langCode,
        children: (
          <Controller
            name={`translations.${langCode}.name`}
            control={control}
            render={({ field }) => (
              <Form.Item
                label={t('knowledgeBase:tags.name')}
                required
                validateStatus={formState.errors.translations ? 'error' : undefined}
                help={
                  formState.errors.translations?.message
                    ? String(formState.errors.translations.message)
                    : undefined
                }
              >
                <Input {...field} placeholder={t('knowledgeBase:tags.name')} />
              </Form.Item>
            )}
          />
        ),
      };
    });
  };

  const onSubmit = async (data: TagFormData): Promise<void> => {
    try {
      const validated = validateLanguageSettings(data.languageSettings);
      if (!validated.valid) {
        message.error(validated.errors.join(', '));
        return;
      }
      const langSettings = sanitizeLanguageSettings(data.languageSettings);

      const translations: Record<string, KBTagTranslation> = {};
      Object.entries(data.translations).forEach(([lang, v]) => {
        if (v?.name?.trim()) {
          translations[lang] = { name: v.name.trim() };
        }
      });

      if (Object.keys(translations).length === 0) {
        message.error(t('knowledgeBase:errors.saveError'));
        return;
      }

      if (isCreate) {
        await createMutation.mutateAsync({
          translations,
          languageSettings: langSettings,
          order: data.order,
          isActive: data.isActive,
        });
        message.success(t('knowledgeBase:messages.tagCreated'));
      } else if (id) {
        await updateMutation.mutateAsync({
          id,
          data: {
            translations,
            languageSettings: langSettings,
            order: data.order,
            isActive: data.isActive,
          },
        });
        message.success(t('knowledgeBase:messages.tagUpdated'));
      }
      navigate('/knowledge-base/tags');
    } catch {
      message.error(t('knowledgeBase:errors.saveError'));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/knowledge-base/tags')}
        >
          {t('common:buttons.back')}
        </Button>
        <h2 style={{ margin: 0 }}>
          {isCreate ? t('knowledgeBase:tags.create') : t('knowledgeBase:tags.edit')}
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card style={{ marginBottom: 24 }}>
          <LanguageSettingsControl control={control} setValue={setValue} />
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Tabs items={getTabItems()} />
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Controller
              name="order"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:tags.order')}>
                  <Input
                    type="number"
                    min={0}
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:tags.active')}>
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    checkedChildren={t('common:common.enabled')}
                    unCheckedChildren={t('common:common.disabled')}
                  />
                </Form.Item>
              )}
            />
          </Space>
        </Card>

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={isPending}
          >
            {t('common:buttons.save')}
          </Button>
          <Button onClick={() => navigate('/knowledge-base/tags')}>
            {t('common:buttons.cancel')}
          </Button>
        </Space>
      </form>
    </div>
  );
};
